import { observer } from 'mobx-react-lite'
import { useLocation } from 'react-router'
import { useEffect, useRef } from 'react'
import { TextareaWithLabel } from 'shared/ui/Textarea'
import { Box, Button } from 'shared/ui'
import { LINK_COMPLIANCE_LEARN_MORE_LINK } from 'shared/constants/links'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { useLocalNumbersStepsContext } from 'pages/settings/pages/compliance/pages/localNumbers/context/localNumbersStepsContext'
import {
  useCaseDescGenerationError,
  useCaseDescMaxTextLength,
  useCaseDescMinTextLength,
} from 'pages/settings/pages/compliance/pages/localNumbers/pages/CampaignUseCase/store/UseCaseStore'
import { PageDescription } from 'pages/settings/ui/PageDescription/PageDescription'
import { LearnMoreLink } from 'pages/settings/ui/LearnMoreLink/LearnMoreLink'
import { AiCheckerNextButtons } from 'pages/settings/pages/compliance/ui/AiCheckerNextButton/AiCheckerNextButtons'
import { RegistrationErrorAlert } from 'pages/settings/pages/compliance/pages/localNumbers/ui/RegistrationErrorAlert/RegistrationErrorAlert'
import { UseCaseSelect } from 'pages/settings/pages/compliance/ui/UseCaseSelect/UseCaseSelect'
import { NextButton } from 'pages/settings/ui/NextButton/NextButton'
import styles from './styles.module.scss'

export const CampaignUseCase = observer(() => {
  const {
    stepLoading,
    useCaseStore,
    aiSidebarStore: {
      isAiCheckrWillUse,
      isLoading: aiLoading,
      isSuccessButton,
      isErrorButton,
      isContinueWithIssues,
    },
  } = useLocalNumbersStepsContext()
  const { featureFlagsStore } = useEntitiesContext()
  const {
    useCaseAsItems,
    useCaseValue,
    useCaseDesc,
    isStep1Disabled,
    setUseCase,
    setUseCaseDesc,
    openUseCaseExampleModal,
    campaignUseCaseNextClick,
    generateAIDesc,
    shouldResetErrors,
    useCaseGenerationError,
    aiGenerationStore: { descLoading, istDescGenerated },
  } = useCaseStore
  const { state } = useLocation()

  const { salesmsg_compliance_ai_v2 } = featureFlagsStore

  const descGenerationText = istDescGenerated ? 'Generate again' : 'Generate with Salesmsg AI'
  const ref = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (state?.focusField && state.focusField === 'useCaseData.description') {
      ref.current?.focus()

      setTimeout(() => {
        ref.current?.blur()
      }, 1000)
    }
  }, [state])

  return (
    <div className={styles.wrap}>
      <PageDescription>
        Select the campaign use case that best describes the type of messaging you plan to send. Add
        a description of your specific texting use case.
        <LearnMoreLink href={LINK_COMPLIANCE_LEARN_MORE_LINK}>Learn more</LearnMoreLink>
      </PageDescription>

      <RegistrationErrorAlert />

      <Box marginTop={5}>
        <UseCaseSelect items={useCaseAsItems} value={useCaseValue || ''} setValue={setUseCase} />
      </Box>

      {useCaseValue && (
        <TextareaWithLabel
          error={useCaseGenerationError}
          ref={ref}
          className={styles.useCaseDesc}
          value={useCaseDesc}
          limit={useCaseDescMaxTextLength}
          minLimit={useCaseDescMinTextLength}
          errorType='bottomText'
          name='description'
          placeholder='Add a description of how the selected use case applies to your business'
          onChange={setUseCaseDesc}
          shouldResetErrors={shouldResetErrors}
          validationMessage={salesmsg_compliance_ai_v2 ? useCaseDescGenerationError : ''}
          labelProps={{
            marginBottom: 0,
            label: {
              text: 'Use case description',
              rightNode: (
                <Button
                  onClick={openUseCaseExampleModal}
                  text={'See example'}
                  size='small'
                  typeBtn='informative'
                />
              ),
            },
          }}
          bottomNode={{
            leftNode: salesmsg_compliance_ai_v2 ? (
              <Button
                onClick={generateAIDesc}
                text={descLoading ? 'Generating...' : descGenerationText}
                size='small'
                typeBtn='informative'
                icon='stars'
                iconProps={{
                  color: 'var(--content-brand-primary)',
                }}
              />
            ) : null,
          }}
        />
      )}
      <div className={styles.stickyActions}>
        {isAiCheckrWillUse ? (
          <AiCheckerNextButtons
            loading={aiLoading}
            disabled={isStep1Disabled || aiLoading}
            text='Check and continue'
            onClick={() => campaignUseCaseNextClick(true)}
            icon='stars'
            isSuccess={isSuccessButton}
            isError={isErrorButton}
            isContinueWithIssues={isContinueWithIssues}
            onContinueWithIssuesClick={() => campaignUseCaseNextClick()}
            isContinueWithIssuesLoading={stepLoading}
          />
        ) : (
          <NextButton
            loading={stepLoading}
            disabled={isStep1Disabled || stepLoading}
            text='Next'
            onClick={() => campaignUseCaseNextClick()}
          />
        )}
      </div>
    </div>
  )
})
